import { useEffect } from 'react';
import ServiceCard from '../../components/base/ServiceCard';
import Button from '../../components/base/Button';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ServiciosPage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Servicios de Vaciado Barcelona - VaciadoPro | Vaciado Pisos y Locales';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios profesionales de vaciado en Barcelona: pisos, locales, trasteros, síndrome Diógenes. Presupuesto gratis. Servicio rápido y eficiente.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'servicios vaciado Barcelona, vaciado pisos Barcelona, vaciado locales Barcelona, vaciado trasteros Barcelona, síndrome Diógenes Barcelona');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/servicios`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Servicios de Vaciado Barcelona",
      "description": "Servicios profesionales de vaciado de pisos, locales y trasteros en Barcelona",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/servicios`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "VaciadoPro Barcelona",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Barcelona",
          "addressRegion": "Cataluña",
          "addressCountry": "ES"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Barcelona"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Catálogo de Servicios de Vaciado",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Pisos",
              "description": "Vaciado completo de viviendas y apartamentos"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Vaciado de Locales",
              "description": "Vaciado de locales comerciales y oficinas"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Trasteros",
              "description": "Limpieza y vaciado de trasteros y almacenes"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Síndrome de Diógenes",
              "description": "Servicios especializados para casos de síndrome de Diógenes"
            }
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const services = [
    {
      title: "Vaciado de Pisos y Apartamentos",
      description: "Servicio completo de vaciado para herencias, mudanzas y alquileres. Nos encargamos de todo el proceso de forma rápida y profesional.",
      image: "https://public.readdy.ai/ai/img_res/f048eb88f2f08c76dc07e6939016a5f5.jpg",
      features: [
        "Vaciado por herencia con máxima discreción",
        "Mudanzas completas y parciales",
        "Liberación de pisos para alquiler",
        "Gestión de documentación importante",
        "Servicio en 24-48 horas"
      ],
      href: "#vaciado-pisos"
    },
    {
      title: "Vaciado de Locales y Oficinas",
      description: "Especialistas en vaciado comercial. Liberamos espacios comerciales y oficinas de forma eficiente, respetando los plazos establecidos.",
      image: "https://readdy.ai/api/search-image?query=Commercial%20office%20space%20clearing%20professional%20workers%20removing%20office%20furniture%20computers%20desks%20chairs%20Barcelona%20business%20environment%20clean%20organized%20efficient%20modern%20lighting&width=400&height=300&seq=vaciado-locales&orientation=landscape",
      features: [
        "Vaciado de oficinas completas",
        "Locales comerciales y almacenes",
        "Gestión de equipos informáticos",
        "Certificados de destrucción de datos",
        "Horarios flexibles y nocturnos"
      ],
      href: "#vaciado-locales"
    },
    {
      title: "Vaciado por Síndrome de Diógenes",
      description: "Servicio especializado y discreto para casos de acumulación compulsiva. Trabajamos con máxima sensibilidad y profesionalidad.",
      image: "https://readdy.ai/api/search-image?query=Sensitive%20professional%20cleaning%20team%20working%20discretely%20in%20cluttered%20space%20Barcelona%20careful%20organized%20approach%20protective%20equipment%20respectful%20workers%20gentle%20lighting%20clean%20background&width=400&height=300&seq=sindrome-diogenes&orientation=landscape",
      features: [
        "Máxima discreción y confidencialidad",
        "Equipo especializado y sensibilizado",
        "Protocolos de seguridad e higiene",
        "Coordinación con servicios sociales",
        "Limpieza y desinfección profunda"
      ],
      href: "#sindrome-diogenes"
    },
    {
      title: "Recogida de Muebles y Electrodomésticos",
      description: "Recogemos muebles y electrodomésticos en cualquier punto de Barcelona. Servicio rápido para particulares y empresas.",
      image: "https://readdy.ai/api/search-image?query=Professional%20furniture%20collection%20service%20Barcelona%20workers%20loading%20large%20appliances%20refrigerator%20sofa%20into%20truck%20efficient%20organized%20team%20modern%20van%20clean%20background&width=400&height=300&seq=recogida-muebles&orientation=landscape",
      features: [
        "Recogida a domicilio en Barcelona",
        "Muebles y electrodomésticos grandes",
        "Servicio el mismo día disponible",
        "Carga y transporte incluido",
        "Sin límite de volumen"
      ],
      href: "#recogida-muebles"
    },
    {
      title: "Gestión de Residuos y Punto Limpio",
      description: "Gestión responsable de todos los residuos. Llevamos los materiales al punto limpio correspondiente siguiendo la normativa.",
      image: "https://readdy.ai/api/search-image?query=Eco-friendly%20waste%20management%20Barcelona%20recycling%20center%20professional%20workers%20sorting%20materials%20clean%20organized%20facility%20green%20environment%20sustainable%20disposal%20modern%20trucks&width=400&height=300&seq=gestion-residuos&orientation=landscape",
      features: [
        "Clasificación y separación de residuos",
        "Transporte a puntos limpios oficiales",
        "Gestión de residuos peligrosos",
        "Certificados de gestión ambiental",
        "Compromiso ecológico 100%"
      ],
      href: "#gestion-residuos"
    },
    {
      title: "Servicios Adicionales",
      description: "Completamos el servicio con limpieza post-vaciado y pequeñas reformas. Todo lo necesario para dejar el espacio listo.",
      image: "https://readdy.ai/api/search-image?query=Post-cleaning%20service%20Barcelona%20professional%20cleaners%20painting%20walls%20empty%20clean%20apartment%20bright%20natural%20lighting%20modern%20tools%20organized%20workers%20finishing%20touches&width=400&height=300&seq=servicios-adicionales&orientation=landscape",
      features: [
        "Limpieza profunda post-vaciado",
        "Pintura y pequeñas reparaciones",
        "Acondicionamiento para alquiler",
        "Certificados de limpieza",
        "Presupuesto todo incluido"
      ],
      href: "#servicios-adicionales"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nuestros servicios de vaciado en Barcelona
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              Soluciones completas para cada necesidad. Más de 5000 servicios realizados con éxito.
              Conoce más sobre 
              <a href="/empresa" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
                nuestra empresa
              </a>
              y resuelve tus dudas en 
              <a href="/faq" className="text-green-200 hover:text-white font-medium ml-1">
                preguntas frecuentes
              </a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                href="https://wa.me/34690220944?text=Hola, necesito información sobre sus servicios de vaciado en Barcelona" 
                variant="primary" 
                size="lg"
                className="flex items-center gap-3"
                target="_blank"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Consultar Servicios
              </Button>
              <Button 
                href="tel:+34690220944" 
                variant="secondary" 
                size="lg"
                className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
              >
                <i className="ri-phone-line text-xl"></i>
                Llamar Ahora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Todos nuestros servicios
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos una gama completa de servicios de vaciado y recogida en Barcelona. 
              Cada servicio está diseñado para satisfacer necesidades específicas con la máxima profesionalidad.
              Ayudamos con el problema de la 
              <a href="https://es.wikipedia.org/wiki/Vivienda_vac%C3%ADa" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-medium">
                vivienda vacía
              </a>
              y trabajamos coordinados con el 
              <a href="https://ajuntament.barcelona.cat/neteja-i-residus/ca/recollida-domestica/sistemes-de-recollida-daltres-tipus-de-residus/recollida-de-mobles-i-trastos" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-medium">
                servicio municipal de recogida de muebles
              </a>
              del Ayuntamiento de Barcelona.
              Si tienes dudas sobre cuál es el servicio que necesitas, consulta nuestras 
              <a href="/faq" className="text-green-600 hover:text-green-700 font-medium ml-1">
                preguntas frecuentes
              </a>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                title={service.title}
                description={service.description}
                image={service.image}
                features={service.features}
                href={service.href}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
              ¿Por qué elegir nuestros servicios?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Totalmente Asegurados</h3>
                <p className="text-gray-600">Seguro de responsabilidad civil y garantía total en todos nuestros servicios.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Servicio Rápido</h3>
                <p className="text-gray-600">Disponibilidad inmediata. Podemos estar en tu ubicación en menos de 2 horas.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">100% Confiable</h3>
                <p className="text-gray-600">Más de 500 clientes satisfechos. Valoración media de 4.9/5 estrellas.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-euro-circle-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Mejor Precio</h3>
                <p className="text-gray-600">Presupuestos sin compromiso. Garantizamos el mejor precio del mercado.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
              Zonas de servicio en Barcelona
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Ofrecemos todos nuestros servicios en toda el área metropolitana de Barcelona
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Barcelona Ciudad</h3>
                <p className="text-gray-600 text-sm">Eixample, Gràcia, Sarrià, Ciutat Vella, Sant Martí, Sants-Montjuïc</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Área Metropolitana</h3>
                <p className="text-gray-600 text-sm">Hospitalet, Badalona, Sabadell, Terrassa, Mataró, Sant Cugat</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Otras Localidades</h3>
                <p className="text-gray-600 text-sm">
                  Consulta disponibilidad para otras zonas de Cataluña. 
                  <a href="/contacto" className="text-green-600 hover:text-green-700 font-medium ml-1">
                    Contáctanos
                  </a> 
                  para más información.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ¿Necesitas alguno de nuestros servicios?
            </h2>
            <p className="text-xl mb-8 text-green-100">
              Contacta con nosotros para un presupuesto gratuito y sin compromiso. 
              Te atendemos las 24 horas, los 7 días de la semana. Conoce más sobre 
              <a href="/empresa" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
                nuestro equipo profesional
              </a>
              y consulta nuestras 
              <a href="/faq" className="text-green-200 hover:text-white font-medium ml-1">
                preguntas frecuentes
              </a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                href="https://wa.me/34690220944?text=Hola, quiero contratar sus servicios de vaciado en Barcelona" 
                variant="primary" 
                size="lg"
                className="flex items-center gap-3"
                target="_blank"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Contratar Servicio
              </Button>
              <Button 
                href="/contacto" 
                variant="secondary" 
                size="lg"
                className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
              >
                <i className="ri-calculator-line text-xl"></i>
                Presupuesto Gratis
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
